import { CustomerOrderOffer } from "src/api/generated/erp/db/types/tables/customerOrderOffer";
import { CustomerOrderOfferView } from "src/api/generated/erp/db/types/tables/customerOrderOfferView.ts";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField";
import { nonNegativeIntegerRule, requireRule } from "src/components/common/forms/validation";
import i18n from "i18next";
import { FormAsyncSelectField } from "src/components/common/forms/fields/FormAsyncSelectField";
import { CustomerApi } from "src/api/generated/erp/sales/customer/api/customerApi.ts";
import { Customer } from "src/api/generated/erp/db/types/tables/customer.ts";
import { AavoButton } from "src/components/common/buttons/AavoButton.tsx";
import { faUsers } from "@fortawesome/pro-regular-svg-icons";
import { CustomerForm } from "src/components/views/erp/sales/customer/CustomerForm.tsx";
import { FormDateField } from "src/components/common/forms/fields/FormDateField.tsx";
import { FormNumberField } from "src/components/common/forms/fields/FormNumberField.tsx";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox.tsx";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { FormAsyncUserSelectField } from "src/components/views/users/FormAsyncUserSelectField.tsx";
import { DeliveryMethod } from "src/api/generated/erp/db/types/tables/deliveryMethod.ts";
import { Currency } from "src/api/generated/erp/db/types/tables/currency.ts";
import { concatWithPipe } from "src/utils/strings.tsx";
import { FormSection } from "src/components/common/forms/FormSection.tsx";
import { CustomerAddress } from "src/api/generated/erp/db/types/tables/customerAddress.ts";
import { CustomerOfferType } from "src/api/generated/erp/db/types/tables/customerOfferType.ts";
import { useState } from "react";
import { useErrorDialog } from "src/components/common/dialogs/errorDialog/ErrorDialogContext.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import {
	getDefaultDeliveryAddress,
	getDefaultInvoiceAddress,
	getDeliveryAddresses,
	getInvoiceAddresses,
} from "src/components/views/erp/sales/customerAddressUtils.ts";
import { AavoFormLayout } from "src/components/common/forms/AavoFormLayout.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { CustomerOfferFormFieldToHideType } from "src/tenantCustomizations/TenantCustomizations.ts";
import { FormCountrySelectField } from "src/components/common/country/FormCountrySelectField.tsx";
import { AavoFormContentParams } from "src/components/common/forms/AavoForm.tsx";
import {
	CustomerOrderOfferEditApi_FormInitData,
} from "src/api/generated/erp/sales/customerOrderOffer/api/customerOrderOfferEditApi.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import {
	ObjectAttributesEmbeddedForm,
	ObjectAttributesEmbeddedFormValues,
} from "src/components/views/erp/objectAttributes/ObjectAttributesEmbeddedForm.tsx";

export interface CustomerOfferFormValues extends CustomerOrderOffer, ObjectAttributesEmbeddedFormValues {
	updateDeliveryDateToLines: boolean;
	updateDeliveryDateToTasks: boolean;
	deliveryAddressId: number;
}

export interface CustomerOfferFormContentProps extends AavoFormContentParams<CustomerOfferFormValues> {
	customerOrderOffer: CustomerOrderOfferView | null | undefined;
	initData: Pick<
		CustomerOrderOfferEditApi_FormInitData,
		| "siteOptions"
		| "paymentTermOptions"
		| "deliveryTermOptions"
		| "deliveryMethodOptions"
		| "contractualTermOptions"
		| "vatCodeOptions"
		| "vatHandlingTypeOptions"
		| "salespersonOptions"
		| "customerPriceGroupOptions"
		| "customerOfferTypeOptions"
		| "currencyOptions"
	>;
	customerAddresses: CustomerAddress[] | null | undefined;
	offerHasAnyLines: boolean;
	hideCustomerFields?: boolean;
}

export const CustomerOfferFormContent = ({
	control,
	setValue,
	trigger,
	watch,
	formState: { dirtyFields },
	customerOrderOffer,
	initData: {
		siteOptions,
		paymentTermOptions,
		contractualTermOptions,
		deliveryTermOptions,
		deliveryMethodOptions,
		vatCodeOptions,
		vatHandlingTypeOptions,
		salespersonOptions,
		customerPriceGroupOptions,
		customerOfferTypeOptions,
		currencyOptions,
	},
	customerAddresses,
	offerHasAnyLines,
	hideCustomerFields,
}: CustomerOfferFormContentProps) => {
	const { logErrorAndShowOnDialog } = useErrorDialog();
	const { openDialog } = useGenericDialog();

	const isExistingRecord = customerOrderOffer != null;
	const offerIsClosed = customerOrderOffer?.isClosed === true;
	const deliveryDateChanged = dirtyFields.plannedDeliveryDate;
	const hasPermission = useHasPermission();

	const [deliveryAddressOptions, setDeliveryAddressOptions] = useState<CustomerAddress[] | undefined>(
		customerAddresses == null ? undefined : getDeliveryAddresses(customerAddresses),
	);

	const [invoiceAddressOptions, setInvoiceAddressOptions] = useState<CustomerAddress[] | undefined>(
		customerAddresses == null ? undefined : getInvoiceAddresses(customerAddresses),
	);
	const { tenantConfig } = useTenantCustomizations();
	const showField = (fieldName: CustomerOfferFormFieldToHideType): boolean => {
		const fieldsToHide = tenantConfig.erp.customerOfferFormFieldsToHide;
		const isInList = fieldsToHide.find((hideFieldName) => hideFieldName == fieldName) != undefined;
		return !isInList;
	};

	return (
		<AavoFormLayout columns={3}>
			{customerOrderOffer?.customerOrderOfferState === "LOST" && (
				<FormTextField
					control={control}
					name={"loseReason"}
					label={i18n.t("lose_reason")}
					spanGridColumns
					multiline
				/>
			)}
			<FormSelectField
				control={control}
				name={"siteId"}
				label={i18n.t("site")}
				options={siteOptions}
				getOptionKey={(site) => site.siteId}
				getOptionLabel={(site) => site.siteName}
				rules={requireRule()}
				disabled={isExistingRecord}
				disableClearable
			/>
			{!hideCustomerFields && (
				<FormAsyncSelectField
					control={control}
					name={"customerId"}
					autoFocus={!isExistingRecord}
					label={i18n.t("customer")}
					getOptionKey={(o: Customer) => o.customerId}
					getOptionLabel={(o) => o.customerName}
					fetchOptions={({ searchQuery, currentSelection }) =>
						CustomerApi.getCustomerSelectionOptions({
							currentSelection: currentSelection,
							searchQuery: searchQuery,
						})
					}
					rules={requireRule()}
					disabled={isExistingRecord}
					onChange={(customer) => onCustomerChanged(customer?.customerId ?? null)}
				/>
			)}
			{!hideCustomerFields && (
				<AavoButton
					label={i18n.t("create_customer")}
					icon={faUsers}
					hidden={isExistingRecord}
					onClick={() => {
						openDialog(({ closeDialog }) => ({
							title: i18n.t("create_customer"),
							size: "xl",
							agentContext: {
								key: defineAgentUiContextKey("erp.sales.customer.customerOfferFormContent.formDialog"),
								pathSegments: [i18n.t("create_customer")],
								data: {
									customerId: null,
									customerOrderOfferId: customerOrderOffer?.customerOrderOfferId ?? null,
								},
							},
							content: (
								<CustomerForm
									onCompleted={async (result) => {
										if (result.type === "success") {
											const customerId = result.value;
											setValue("customerId", customerId);
											await onCustomerChanged(customerId);
										}
										await closeDialog();
									}}
								/>
							),
						}));
					}}
				/>
			)}
			<FormDateField
				control={control}
				name={"validUntil"}
				label={i18n.t("valid_until")}
				disabled={offerIsClosed}
				onChange={async () => {
					await trigger("validForDays");
				}}
				rules={{
					validate: (_, values) => validateValidUntilAndValidForFields(values),
				}}
			/>
			<FormNumberField
				control={control}
				name={"validForDays"}
				label={i18n.t("valid_for_days")}
				disabled={offerIsClosed}
				onChange={async () => {
					await trigger("validUntil");
				}}
				rules={{
					validate: (_, values) => validateValidUntilAndValidForFields(values),
				}}
			/>
			{isExistingRecord && (
				<FormCheckbox control={control} name={"frozen"} label={i18n.t("frozen")} disabled={offerIsClosed} />
			)}
			<FormTextField
				control={control}
				name={"orderReference"}
				label={i18n.t("reference")}
				disabled={offerIsClosed}
				startNewGridRow
			/>
			<FormSelectField
				control={control}
				name={"customerOfferTypeId"}
				label={i18n.t("customer_offer_type")}
				options={customerOfferTypeOptions}
				getOptionKey={(type: CustomerOfferType) => type.customerOfferTypeId}
				getOptionLabel={(type) => type.name}
				disabled={offerIsClosed}
			/>
			{showField("customerOfferRequestNo") && (
				<FormTextField
					control={control}
					name={"customerOfferRequestNo"}
					label={i18n.t("customer_offer_request_no")}
					disabled={offerIsClosed}
				/>
			)}
			{showField("externalOrderId") && (
				<FormTextField
					control={control}
					name={"externalOrderId"}
					label={i18n.t("external_order_id")}
					disabled={offerIsClosed}
				/>
			)}
			<FormDateField
				control={control}
				name={"plannedDeliveryDate"}
				label={i18n.t("planned_delivery_date")}
				disabled={offerIsClosed}
				rules={requireRule()}
			/>
			{isExistingRecord && deliveryDateChanged && (
				<FormCheckbox
					control={control}
					name={"updateDeliveryDateToLines"}
					label={i18n.t("update_also_to_lines")}
					spanGridColumns
				/>
			)}
			{isExistingRecord && deliveryDateChanged && (
				<FormCheckbox
					control={control}
					name={"updateDeliveryDateToTasks"}
					label={i18n.t("update_also_to_tasks")}
					spanGridColumns
				/>
			)}
			<FormAsyncUserSelectField
				control={control}
				name={"responsiblePersonId"}
				label={i18n.t("responsible_person")}
				rules={requireRule()}
				disabled={offerIsClosed}
				startNewGridRow
			/>
			<FormSelectField
				control={control}
				name={"salespersonId"}
				label={i18n.t("salesperson")}
				options={salespersonOptions}
				getOptionKey={(o) => o.salespersonId}
				getOptionLabel={(o) => o.name}
				disabled={offerIsClosed || !hasPermission(Permissions.Sales.CustomerOffers.MANAGE_ALL)}
			/>
			{showField("currencyId") && (
				<FormSelectField
					control={control}
					name={"currencyId"}
					label={i18n.t("currency")}
					options={currencyOptions}
					getOptionKey={(currency: Currency) => currency.currencyId}
					getOptionLabel={(currency) => currency.currencyCode}
					disabled={isExistingRecord || offerIsClosed}
					disableClearable
				/>
			)}
			{showField("deliveryTermsId") && (
				<FormSelectField
					control={control}
					name={"deliveryTermsId"}
					label={i18n.t("delivery_term")}
					options={deliveryTermOptions}
					getOptionKey={(o) => o.deliveryTermsId}
					getOptionLabel={(o) => `${o.deliveryTermsCode} - ${o.deliveryTermsDescription}`}
					rules={requireRule()}
					disabled={offerIsClosed}
					disableClearable
				/>
			)}
			{showField("deliveryTermsDestination") && (
				<FormTextField
					control={control}
					name={"deliveryTermsDestination"}
					label={i18n.t("delivery_terms_destination")}
					disabled={offerIsClosed}
				/>
			)}
			{showField("deliveryMethodId") && (
				<FormSelectField
					control={control}
					name={"deliveryMethodId"}
					label={i18n.t("delivery_method")}
					options={deliveryMethodOptions}
					getOptionKey={(o) => o.deliveryMethodId}
					getOptionLabel={(o) => o.deliveryMethodCode}
					rules={requireRule()}
					onChange={(_, deliveryMethod: DeliveryMethod | null) => {
						setValue("transportDuration", deliveryMethod?.transportDuration ?? null);
					}}
					disabled={offerIsClosed}
					disableClearable
				/>
			)}
			<FormSelectField
				control={control}
				name={"invoiceAddressId"}
				label={i18n.t("invoice_address")}
				rules={invoiceAddressOptions != null ? requireRule() : undefined}
				options={invoiceAddressOptions ?? []}
				getOptionKey={(o) => o.customerAddressId}
				getOptionLabel={(o) => concatWithPipe(o.address_1, o.address_2, o.city)}
				disabled={invoiceAddressOptions == null || offerIsClosed}
			/>
			<FormSelectField
				control={control}
				name={"paymentTermId"}
				label={i18n.t("payment_term")}
				options={paymentTermOptions}
				getOptionKey={(o) => o.paymentTermId}
				getOptionLabel={(o) => o.paymentTerm ?? ""}
				rules={requireRule()}
				disabled={offerIsClosed}
				disableClearable
			/>
			<FormDateField
				control={control}
				name={"contractDate"}
				label={i18n.t("contract_date")}
				readOnly={tenantConfig.erp.customerOfferContractSigningEnabled}
			/>
			<FormSelectField
				control={control}
				name={"contractualTermId"}
				label={i18n.t("contractual_term")}
				options={contractualTermOptions}
				getOptionKey={(o) => o.contractualTermId}
				getOptionLabel={(o) => o.code}
				disabled={offerIsClosed}
			/>
			<FormSelectField
				control={control}
				name={"customerPriceGroupId"}
				label={i18n.t("price_group")}
				options={customerPriceGroupOptions}
				getOptionKey={(o) => o.customerPriceGroupId}
				getOptionLabel={(o) => o.name}
				disabled={offerIsClosed}
				rules={requireRule()}
			/>
			{showField("vatHandlingTypeId") && (
				<FormSelectField
					control={control}
					name={"vatHandlingTypeId"}
					label={i18n.t("vat_handling_type")}
					options={vatHandlingTypeOptions}
					getOptionKey={(o) => o.vatHandlingTypeId}
					getOptionLabel={(o) => o.name}
					rules={requireRule()}
					disabled={offerIsClosed}
					disableClearable
				/>
			)}
			{showField("inputPricesWithVat") && (
				<FormCheckbox
					control={control}
					name={"inputPricesWithVat"}
					label={i18n.t("input_prices_with_vat")}
					disabled={offerIsClosed || offerHasAnyLines}
				/>
			)}
			{showField("billingPlanEnabled") && (
				<FormCheckbox
					control={control}
					name={"billingPlanEnabled"}
					label={i18n.t("billing_plan_enabled")}
					disabled={offerIsClosed}
					startNewGridRow
				/>
			)}
			{showField("vatCodeId") &&
				watch("billingPlanEnabled") &&
				vatHandlingTypeOptions.find((o) => o.vatHandlingTypeId === watch("vatHandlingTypeId"))?.vatPercent ==
					null && (
					<FormSelectField
						control={control}
						name={"vatCodeId"}
						label={i18n.t("vat_code")}
						options={vatCodeOptions}
						getOptionKey={(o) => o.vatCodeId}
						getOptionLabel={(o) => o.vatCodeName}
						disabled={offerIsClosed}
						disableClearable
					/>
				)}
			<FormTextField
				control={control}
				name={"contactName"}
				label={i18n.t("contact_person")}
				disabled={offerIsClosed}
				startNewGridRow
			/>
			<FormTextField
				control={control}
				name={"contactEmail"}
				label={i18n.t("contact_email")}
				disabled={offerIsClosed}
			/>
			<FormTextField
				control={control}
				name={"contactPhone"}
				label={i18n.t("contact_phone")}
				disabled={offerIsClosed}
			/>
			<FormTextField
				control={control}
				name={"note"}
				label={i18n.t("customer_offer_form.note")}
				spanGridColumns
				multiline
				disabled={offerIsClosed}
			/>
			<FormTextField
				control={control}
				name={"additionalAddress"}
				label={i18n.t("customer_offer_form.additional_address")}
				spanGridColumns
			/>
			<FormSection label={i18n.t("delivery_address")}>
				{!isExistingRecord && (
					<FormSelectField
						control={control}
						name={"deliveryAddressId"}
						label={i18n.t("delivery_address")}
						rules={deliveryAddressOptions != null ? requireRule() : undefined}
						options={deliveryAddressOptions ?? []}
						getOptionKey={(o: CustomerAddress) => o.customerAddressId}
						getOptionLabel={(o) => concatWithPipe(o.address_1, o.city)}
						onChange={(_, address) => {
							onDeliveryAddressChanged(address);
						}}
						disabled={deliveryAddressOptions == null}
						disableClearable
					/>
				)}
				<FormTextField
					control={control}
					name={"deliveryName"}
					label={i18n.t("address_description")}
					disabled={offerIsClosed}
				/>
				<FormTextField
					control={control}
					name={"deliveryRecipientName"}
					label={i18n.t("recipient")}
					disabled={offerIsClosed}
				/>
				<FormTextField
					control={control}
					name={"deliveryAddress_1"}
					label={i18n.t("address_1")}
					rules={requireRule()}
					disabled={offerIsClosed}
				/>
				<FormTextField
					control={control}
					name={"deliveryAddress_2"}
					label={i18n.t("address_2")}
					disabled={offerIsClosed}
				/>
				<FormTextField
					control={control}
					name={"deliveryPostalCode"}
					label={i18n.t("postal_code")}
					rules={requireRule()}
					disabled={offerIsClosed}
				/>
				<FormTextField
					control={control}
					name={"deliveryCity"}
					label={i18n.t("city")}
					rules={requireRule()}
					disabled={offerIsClosed}
				/>
				<FormCountrySelectField
					control={control}
					name={"deliveryCountry"}
					label={i18n.t("country")}
					disabled={offerIsClosed}
					disableClearable
				/>
				<FormTextField
					control={control}
					name={"transportDuration"}
					label={i18n.t("transport_duration")}
					rules={nonNegativeIntegerRule()}
					disabled={offerIsClosed}
				/>
				<FormTextField
					control={control}
					name={"deliveryContact"}
					label={i18n.t("contact_information")}
					disabled={offerIsClosed}
				/>
			</FormSection>
			{(watch("attributes")?.length ?? 0) > 0 && (
				<FormSection label={i18n.t("attributes")}>
					<ObjectAttributesEmbeddedForm control={control} disabled={offerIsClosed} />
				</FormSection>
			)}
		</AavoFormLayout>
	);

	async function onCustomerChanged(customerId: number | null) {
		try {
			if (customerId == null) {
				setDeliveryAddressOptions(undefined);
				setInvoiceAddressOptions(undefined);
				return;
			}
			const { customer, deliveryAddresses, invoiceAddresses } = await CustomerApi.getCustomerWithAddresses({
				customerId: customerId,
			});

			setDeliveryAddressOptions(deliveryAddresses);
			setInvoiceAddressOptions(invoiceAddresses);

			const defaultDeliveryAddress = getDefaultDeliveryAddress(deliveryAddresses);
			setValue("deliveryAddressId", defaultDeliveryAddress?.customerAddressId);
			onDeliveryAddressChanged(defaultDeliveryAddress);

			const defaultInvoiceAddress = getDefaultInvoiceAddress(invoiceAddresses);
			setValue("invoiceAddressId", defaultInvoiceAddress.customerAddressId);

			setValue("paymentTermId", customer.paymentTermId);
			setValue("currencyId", customer.currencyId);
			if (customer.customerPriceGroupId != null) {
				setValue("customerPriceGroupId", customer.customerPriceGroupId);
			}
			setValue(
				"deliveryTermsId",
				customer.deliveryTermsId ?? deliveryTermOptions.find((term) => term.isDefault)?.deliveryTermsId,
			);
			setValue("deliveryTermsDestination", customer.deliveryTermsDestination ?? "");
			setValue("contactName", defaultDeliveryAddress.contact ?? "");
			setValue("contactEmail", defaultDeliveryAddress.email ?? "");
			setValue("contactPhone", defaultDeliveryAddress.phone ?? "");
		} catch (e) {
			logErrorAndShowOnDialog(e);
		}
	}

	function onDeliveryAddressChanged(address: CustomerAddress | null) {
		setValue("deliveryName", address?.name ?? "");
		setValue("deliveryRecipientName", address?.recipientName ?? "");
		setValue("deliveryAddress_1", address?.address_1 ?? "");
		setValue("deliveryAddress_2", address?.address_2 ?? "");
		setValue("deliveryPostalCode", address?.postalCode ?? "");
		setValue("deliveryCity", address?.city ?? "");
		setValue("deliveryCountry", address?.country ?? "FINLAND");
		setValue("deliveryContact", address == null ? "" : `${address.contact} ${address.phone}`);
		if (address?.vatHandlingTypeId != null) {
			setValue("vatHandlingTypeId", address.vatHandlingTypeId);
		}
	}

	function validateValidUntilAndValidForFields(values: CustomerOfferFormValues) {
		if (values.validUntil == null && values.validForDays == null) {
			return i18n.t("valid_until_or_valid_for_days_required");
		}
		if (values.validUntil != null && values.validForDays != null) {
			return i18n.t("valid_until_and_valid_for_days_cannot_both_be_set");
		}
	}
};
